import { useState } from 'react'
import api from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import { FileSpreadsheet, Lock, Loader2, ShieldCheck } from 'lucide-react'

/**
 * Decrypted personal data as a password-protected Excel file. For Admins and anyone an Admin
 * granted "Export personal data" on People & Access. The server holds the same rule
 * (routers/personal_export.py); this page only spares everyone else a door that does not open.
 */
export default function PersonalDataExport() {
  const { user } = useAuth()
  const allowed = user?.role === 'Admin' || user?.is_super_admin
    || (user?.capabilities || []).includes('export_personal_data')
  const [reason, setReason] = useState('')
  const [password, setPassword] = useState('')
  const [again, setAgain] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(false)

  if (!allowed) {
    return <div className="p-6 text-gray-500">You do not have the “Export personal data” permission. An Admin can give it on People &amp; Access.</div>
  }

  const ready = reason.trim().length >= 10 && password.length >= 12 && password === again

  const run = async (e) => {
    e.preventDefault()
    setBusy(true); setError(null); setDone(false)
    try {
      const r = await api.post('/admin/personal-data/export', { reason: reason.trim(), password }, { responseType: 'blob' })
      const name = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'ProTrack-personal-data.xlsx'
      const url = URL.createObjectURL(r.data)
      const a = document.createElement('a')
      a.href = url; a.download = name
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
      setPassword(''); setAgain(''); setDone(true)
    } catch (err) {
      // The error body arrives as a blob too: read it to show the server's own reason.
      let msg = 'Export failed'
      try { msg = JSON.parse(await err.response.data.text()).detail || msg } catch { /* keep the default */ }
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      <div className="bg-linear-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold flex items-center gap-2"><FileSpreadsheet className="w-6 h-6" /> Personal Data Export</h1>
        <p className="mt-1 opacity-90 text-sm">Every consultant’s personal and bank details, decrypted, in an Excel file locked with your password.</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900 space-y-1">
        <p className="font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Before you export</p>
        <p>Your name, the time, your reason and the number of people are recorded in the audit log.</p>
        <p>The file opens only with the password you choose here. ProTrack does not keep the password or the file: keep the password apart from the file, never in the same email.</p>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
      {done && <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-3 text-sm">Downloaded. Delete the file when you have finished with it.</div>}

      <form onSubmit={run} className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Why do you need this export?</span>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="e.g. Annual check of bank details before the payment run" />
          <span className="text-xs text-gray-400">At least 10 characters. Kept in the audit log.</span>
        </label>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700 flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> File password</span>
            <input type="password" autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            <span className="text-xs text-gray-400">At least 12 characters.</span>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Repeat the password</span>
            <input type="password" autoComplete="new-password" value={again} onChange={e => setAgain(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            {again && password !== again && <span className="text-xs text-red-600">The passwords differ.</span>}
          </label>
        </div>
        <button type="submit" disabled={!ready || busy}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
          Export to Excel
        </button>
      </form>
    </div>
  )
}
